import React, { useState, MouseEventHandler } from 'react';
import styled, { css } from 'styled-components';
import { Close16, Download16 } from '@honeybook/icons';
import { applyOpacity } from '../../utils/color.utils';
import { vars } from '../../theme';
import HBFileAttachmentProgressBar from '../HBFileAttachment/HBFileAttachmentProgressBar';
import HBIconButton from '../HBIconButton/HBIconButton';
import { addDefaultFlagsToCloudinaryUrl } from '../HBMediaElement/HBMediaElement';
import { IconButton } from '../HBIconButton/HBIconButton.styles';
import HBText from '../HBText/HBText';
import { MustIncludeOneOfBasicTestIdWithIdProps } from '../common/internal/test-id.types';
import { useBuildTestIdInternal } from '../../hooks/useBuildTestIdInternal';
import HBClickable from '../HBClickable/HBClickable';
import { transition } from '../../styles/animations';
import {
    generateImageDownloadUrl,
    generatePdfThumbnailUrl
} from '../common/image.utils';
import HBFilePreview from '../HBFilePreview/HBFilePreview';
import withStopPropagation from '../common/withStopPropagation';
import HBLinkButton from '../HBLinkButton/HBLinkButton';

const ATTACHMENT_THUMBNAIL_SIZE = 58;
const ATTACHMENT_WIDTH = 184;

const isPdfExtension = (extension: string) => {
    if (!extension) {
        return;
    }
    const regex = new RegExp('pdf', 'i');
    return regex.test(extension);
};

const getAttachmentThumbnail = ({
    isPdf,
    url
}: {
    url: string;
    isPdf?: boolean;
}) => {
    if (isPdf) {
        return generatePdfThumbnailUrl({
            url,
            width: ATTACHMENT_THUMBNAIL_SIZE,
            height: ATTACHMENT_THUMBNAIL_SIZE
        });
    }
    const transformations = [
        {
            quality: 'auto',
            fetchFormat: 'auto',
            flags: 'lossy',
            crop: 'thumb',
            width: ATTACHMENT_THUMBNAIL_SIZE,
            height: ATTACHMENT_THUMBNAIL_SIZE,
            dpr: 3
        }
    ];

    return addDefaultFlagsToCloudinaryUrl(url, transformations) || url;
};

export interface Props {
    /** Override for the tile background color; defaults to transparent. */
    backgroundColor?: string;
    /** CSS class applied to the root element. */
    className?: string;
    /** Name of the attached file; shown as text in the tile and used to derive the file extension. */
    fileName?: string;
    /** Determines whether the attachment is treated as a generic file or an image, affecting thumbnail generation and download behavior. */
    fileType: 'file' | 'image';
    /** Cloudinary or direct URL used for the thumbnail preview and download. */
    fileUrl?: string;
    /** Hides the remove (X) button; the button is also auto-hidden while upload is in progress. */
    hideRemoveButton?: boolean;
    /** Disables all click, download, and remove interactions. @default false */
    disabled?: boolean;
    /** Enables the download action overlay on hover. */
    canDownload?: boolean;
    /** Identifier for this attachment; passed to `onRemove` when the remove button is clicked. */
    id: string;
    /** Called with the attachment `id` when the remove button is clicked. */
    onRemove?: (id: string) => void;
    /** Controls tile proportions. 'sm' renders a compact tile; 'md' (default) is the standard 184px tile. */
    size?: 'md' | 'sm';
    /** Upload progress percentage (0-100); values below 100 show a progress bar and dim the content. */
    uploadProgress?: number;
    /** Error message displayed below the attachment when upload fails; enables the retry button when `onRetry` is also provided. */
    uploadError?: string;
    /** Callback for the retry action shown when `uploadError` is set. */
    onRetry?: () => void;
    /** Enables opening a full-screen file preview overlay when the attachment is clicked. */
    canPreview?: boolean;
    /** Called when the attachment is clicked, receives `{ isFile }` indicating whether `fileType` is 'file'. */
    onClick?: ({ isFile }: { isFile?: boolean }) => void;
    /** Adds a blue box-shadow on hover. */
    withBoxShadow?: boolean;
    /** When true, shows an image/PDF thumbnail preview instead of the file extension text. */
    shouldShowFilePreviewThumbnail?: boolean;
}

const HBFileAttachment = ({
    backgroundColor,
    className,
    fileName,
    fileType,
    fileUrl,
    hideRemoveButton = false,
    id,
    testIdName,
    testId,
    onRemove,
    uploadProgress,
    uploadError,
    onRetry,
    disabled = false,
    onClick,
    canDownload = true,
    canPreview = true,
    withBoxShadow = true,
    shouldShowFilePreviewThumbnail = true
}: Props & MustIncludeOneOfBasicTestIdWithIdProps) => {
    const [isPreviewShown, setIsPreviewShown] = useState(false);

    const fileRegex = fileName?.match(/\.([0-9a-zA-Z]+)(?:[\\?#]|$)/);
    const fileExtension = fileRegex?.[1];

    const isRemovable =
        !hideRemoveButton && (!uploadProgress || uploadProgress === 100);
    const isFile = fileType === 'file';
    const isPdf = isPdfExtension(fileExtension);
    const imageThumbnail = uploadProgress
        ? null
        : getAttachmentThumbnail({ url: fileUrl, isPdf });

    const finalTestId = useBuildTestIdInternal({
        testId,
        identifier: testIdName || id,
        suffix: 'attachment',
        componentName: 'HBFileAttachment'
    });

    const handleClick = () => {
        onClick?.({ isFile });
        setIsPreviewShown(true);
    };

    const handleDownload = () => {
        window.location.href =
            fileType === 'file' ? fileUrl : generateImageDownloadUrl(fileUrl);
    };

    const isDownloadIconShown = !uploadProgress && !uploadError && canDownload;

    const shouldShowAction = isDownloadIconShown && !disabled;

    const onClickRemove: MouseEventHandler<HTMLButtonElement> = e => {
        e.stopPropagation();
        onRemove(id);
    };

    const shouldShowFileExtension =
        !shouldShowFilePreviewThumbnail || (isFile && !isPdf) || !fileUrl;

    return (
        <>
            <Container>
                <InputContainer
                    id={id}
                    isDisabled={disabled}
                    className={className}
                    testId={finalTestId}
                    onClick={disabled ? () => {} : handleClick}
                    $backgroundColor={backgroundColor}
                    $uploadError={uploadError}
                    $withBoxShadow={withBoxShadow}
                >
                    <Content isDimmed={uploadProgress < 100}>
                        <PreviewContainer>
                            {shouldShowAction && (
                                <QuickActionOverlay>
                                    <HBIconButton
                                        buttonTheme="light-invisible"
                                        icon={Download16}
                                        testIdName="download"
                                        onClick={withStopPropagation(
                                            handleDownload
                                        )}
                                        label="download"
                                    />
                                </QuickActionOverlay>
                            )}
                            {shouldShowFileExtension ? (
                                <HBText
                                    type="subtitle14"
                                    color="text.secondary"
                                >
                                    {fileExtension || 'FILE'}
                                </HBText>
                            ) : (
                                <Image
                                    onClick={
                                        isFile || disabled
                                            ? undefined
                                            : handleClick
                                    }
                                    url={imageThumbnail}
                                    testIdName="image-preview"
                                    isDisabled={disabled}
                                />
                            )}
                        </PreviewContainer>
                        {fileName && (
                            <Details title={fileName} noRemove={isRemovable}>
                                <FileTitle>{fileName}</FileTitle>
                                {!!uploadError && !!onRetry && (
                                    <RetryWrapper>
                                        <Retry
                                            onClick={onRetry}
                                            testIdName="retry"
                                            size="sm"
                                        >
                                            Retry
                                        </Retry>
                                    </RetryWrapper>
                                )}
                            </Details>
                        )}
                    </Content>

                    {isRemovable && (
                        <RemoveAttachment
                            icon={Close16}
                            label={`Remove attachment ${fileName}`}
                            onClick={onClickRemove}
                            size="xs"
                            testId={
                                finalTestId
                                    ? `${finalTestId}-remove`
                                    : undefined
                            }
                        />
                    )}

                    {uploadProgress < 100 && (
                        <ProgressBarContainer>
                            <HBFileAttachmentProgressBar
                                percent={uploadProgress}
                            />
                        </ProgressBarContainer>
                    )}
                </InputContainer>

                {!!uploadError && (
                    <InvalidWrapper>
                        <HBText color="text.danger" type="body12">
                            {uploadError}
                        </HBText>
                    </InvalidWrapper>
                )}
            </Container>
            {isPreviewShown && canPreview && (
                <HBFilePreview
                    fileName={fileName}
                    fileUrl={fileUrl}
                    fileType={fileType}
                    onClose={() => setIsPreviewShown(false)}
                    onDownload={canDownload ? handleDownload : null}
                />
            )}
        </>
    );
};

export default HBFileAttachment;

// -------------------------------- Styles -------------------------------- //
const QuickActionOverlay = styled.div`
    background-color: ${applyOpacity(
        vars.colors.surface.containerInverted,
        70
    )};
    ${transition('opacity')};
    opacity: 0;
    position: absolute;
    width: inherit;
    height: inherit;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const Container = styled.div`
    display: flex;
    flex-direction: column;
`;

const InputContainer = styled(HBClickable)<{
    $backgroundColor?: string;
    $uploadError?: string;
    $withBoxShadow?: boolean;
}>`
    width: ${ATTACHMENT_WIDTH}px;
    display: flex;
    gap: 4px;
    font-family: ${vars.fonts.body};
    border-radius: ${vars.radius.xs};
    border: solid 1px
        ${({ $uploadError }) =>
            $uploadError
                ? vars.colors.stroke.danger
                : vars.colors.stroke.secondary};
    position: relative;
    overflow: hidden;
    ${transition('box-shadow')};
    background-color: ${props => props.$backgroundColor || 'transparent'};

    @media (hover) {
        &:hover {
            ${QuickActionOverlay} {
                opacity: 1;
            }
            ${({ $withBoxShadow }) =>
                $withBoxShadow &&
                css`
                    box-shadow: ${vars.shadow['30']};
                `};
        }
    }
`;

const Content = styled.div<{ isDimmed: boolean }>`
    display: flex;
    flex: 1;
    flex-direction: row;
    justify-content: space-between;
    align-items: stretch;
    transition: opacity 0.2s linear;
    ${({ isDimmed }) =>
        isDimmed &&
        css`
            opacity: 0.4;
        `}
`;

const InvalidWrapper = styled.div`
    padding-block-start: 4px;
    display: flex;
`;

const RetryWrapper = styled.div`
    flex: 1;
    display: flex;
    justify-content: flex-end;
`;

const Retry = styled(HBLinkButton)`
    align-self: flex-end;
    margin-left: 6px;
    cursor: pointer;
`;

const RemoveAttachment = styled(HBIconButton)`
    position: absolute;
    top: 0;
    right: 0;
    height: 40px;
    ${IconButton} {
        &:hover {
            background: transparent;
        }
        cursor: pointer;
    }
`;

const ProgressBarContainer = styled.div`
    position: absolute;
    bottom: 0;
    width: 100%;
`;

const PreviewContainer = styled.div`
    width: 58px;
    height: 58px;
    background: ${vars.colors.surface.neutral};
    display: flex;
    justify-content: center;
    align-items: center;
`;

const Image = styled(HBClickable)<{
    url: string;
}>`
    background-image: url(${props => props.url});
    border-radius: ${vars.radius.xs} 0 0 ${vars.radius.xs};
    background-size: cover;
    background-repeat: no-repeat;
    width: 100%;
    height: 100%;
`;

const FileTitle = styled.div`
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 12px;
    font-weight: 600;
`;

const Details = styled.div<{ noRemove: boolean }>`
    padding: 8px 8px 0;
    width: 88px;
    ${({ noRemove }) =>
        !noRemove &&
        css`
            padding-right: 8px;
            width: 110px;
        `};
    white-space: nowrap;
    flex-grow: 1;
    display: flex;
    flex-direction: column;
`;
